/**
 * client/src/components/layout/pending-invitations-provider.tsx
 *
 * The single fetch owner for the pending-invitation counts (#2171).
 *
 * Mounted once, high in the authenticated shell, so the nav badge, the
 * Invitations page and the Connections pointer all read ONE value. Doing the
 * fetching here rather than inside `useVisibleNavItems` is what keeps the nav
 * builders pure display logic — they read a context with a safe default, so a
 * nav rendered outside the shell shows no badge instead of crashing on a
 * missing QueryClient.
 *
 * All four reads are the SAME queries (same keys) the Invitations page's own
 * sections make — and the guardianship one is the key the Guardian Setup page
 * has always used — so this costs no extra network: react-query serves every
 * reader from one cache entry, and an accept/decline invalidating a key updates
 * the badge and the list together.
 */

import { useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { getIncomingConnectionRequests, getIncomingOrgConnectionRequests } from "@/lib/connections-api";
import { countPendingGuardianships, getGuardianships } from "@/lib/guardianships-api";
import {
  CONNECTION_REQUESTS_QUERY_KEY,
  GUARDIANSHIPS_QUERY_KEY,
  MEMBERSHIP_REQUESTS_QUERY_KEY,
  ORG_CONNECTION_REQUESTS_QUERY_KEY,
  PendingInvitationsContext,
  type PendingInvitationCounts,
} from "@/lib/pending-invitations";
import { getIncomingMembershipRequests } from "@/lib/team-members-api";

export function PendingInvitationsProvider({ children }: { children: ReactNode }): React.ReactElement {
  const membership = useQuery({
    queryKey: MEMBERSHIP_REQUESTS_QUERY_KEY,
    queryFn: getIncomingMembershipRequests,
  });
  const connection = useQuery({
    queryKey: CONNECTION_REQUESTS_QUERY_KEY,
    queryFn: getIncomingConnectionRequests,
  });
  const orgConnection = useQuery({
    queryKey: ORG_CONNECTION_REQUESTS_QUERY_KEY,
    queryFn: getIncomingOrgConnectionRequests,
  });

  // The guardianship read returns every lifecycle state; only `pending` rows
  // are an unanswered question, so only those are counted (see
  // `countPendingGuardianships`).
  const guardianships = useQuery({
    queryKey: GUARDIANSHIPS_QUERY_KEY,
    queryFn: getGuardianships,
  });

  const organisation = membership.data?.requests?.length ?? 0;
  const connectionCount = (connection.data?.requests?.length ?? 0) + (orgConnection.data?.requests?.length ?? 0);
  const guardian = countPendingGuardianships(guardianships.data);
  const isLoading = membership.isLoading || connection.isLoading || orgConnection.isLoading || guardianships.isLoading;
  // A failed read resolves to `data: undefined`, which counts as zero. Left
  // unreported, that would let the Invitations page state "no invitations
  // right now" on the strength of a request that never came back.
  const isError = membership.isError || connection.isError || orgConnection.isError || guardianships.isError;

  // Memoised because context propagates by reference: an unmemoised literal
  // re-renders every consumer — both navs, the org switcher, the Connections
  // pointer, the page — on every render of this provider, including the two
  // extra passes as the queries settle at different times and any background
  // refetch that returns identical counts.
  const value = useMemo<PendingInvitationCounts>(
    () => ({
      organisation,
      connection: connectionCount,
      guardian,
      total: organisation + connectionCount + guardian,
      isLoading,
      isError,
    }),
    [organisation, connectionCount, guardian, isLoading, isError],
  );

  return (
    <PendingInvitationsContext.Provider value={value}>
      {children}
    </PendingInvitationsContext.Provider>
  );
}
